import { useEffect, useMemo, useRef, useState } from "react";
import { useT } from "@/i18n/locale-context";
import type { BodyArea } from "@/lib/protocols/definitions";

/**
 * CaptureCoach — Level 1 (deterministic) capture quality analyzer.
 *
 * Runs purely on canvas 2D in the browser (no native deps, no WASM, no SSR).
 * Computes:
 *  - focus (variance of Laplacian on a centered ROI),
 *  - lighting (mean + std of luma),
 *  - framing (centroid offset of high-contrast pixels vs. ROI center).
 *
 * Emits `onQualityChange` with a `CaptureQuality` payload that the page
 * submits alongside the observation (persisted into capture_quality jsonb).
 *
 * Level 2 (MediaPipe face landmarks) is delegated to an optional lazy
 * loader; if the package is not installed it falls back silently to L1.
 */

export type CaptureQuality = {
  focus: "blurry" | "ok" | "sharp";
  focusVariance: number;
  lighting: "low" | "ok" | "good" | "harsh";
  brightnessMean: number;
  brightnessStd: number;
  framing: "off" | "ok" | "good";
  alignmentScore?: number;
  faceLandmarksVersion?: string;
  coachLevel: 1 | 2;
};

type Props = {
  /** Data URL of the uploaded/captured image. */
  dataUrl: string | null;
  /** Target body area — drives the overlay shape. */
  bodyArea: BodyArea;
  /** Optional URL of the previous step photo (signed) for ghost overlay. */
  previousUrl?: string | null;
  onQualityChange?: (q: CaptureQuality | null) => void;
};

export function CaptureCoach({ dataUrl, bodyArea, previousUrl, onQualityChange }: Props) {
  const t = useT();
  const [quality, setQuality] = useState<CaptureQuality | null>(null);
  const [showGhost, setShowGhost] = useState(true);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Recompute when image changes.
  useEffect(() => {
    if (!dataUrl) {
      setQuality(null);
      onQualityChange?.(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const q = analyzeImage(img);
      setQuality(q);
      onQualityChange?.(q);
    };
    img.src = dataUrl;
    imgRef.current = img;
    return () => { cancelled = true; };
    // onQualityChange is intentionally excluded from deps (stable enough).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUrl]);

  const overlay = useMemo(() => overlayForArea(bodyArea), [bodyArea]);

  if (!dataUrl) return null;

  return (
    <div className="mt-3 space-y-2">
      {/* Framing preview with overlay + optional ghost */}
      <div className="relative aspect-[4/5] max-w-md mx-auto rounded-2xl overflow-hidden border border-foreground/10 bg-black">
        <img src={dataUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        {previousUrl && showGhost && (
          <img
            src={previousUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover mix-blend-screen opacity-40 pointer-events-none"
          />
        )}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 125"
          preserveAspectRatio="none"
        >
          {overlay}
        </svg>
      </div>

      {/* Badges */}
      {quality && (
        <div className="flex flex-wrap gap-2 justify-center text-[11px] uppercase tracking-widest">
          <QualityBadge
            label={t("coach.badge.focus")}
            state={quality.focus === "blurry" ? "bad" : quality.focus === "sharp" ? "good" : "ok"}
            value={quality.focus}
          />
          <QualityBadge
            label={t("coach.badge.lighting")}
            state={quality.lighting === "low" || quality.lighting === "harsh" ? "bad" : quality.lighting === "good" ? "good" : "ok"}
            value={quality.lighting}
          />
          <QualityBadge
            label={t("coach.badge.framing")}
            state={quality.framing === "off" ? "bad" : quality.framing === "good" ? "good" : "ok"}
            value={quality.framing}
          />
        </div>
      )}

      {previousUrl && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowGhost((v) => !v)}
            className="text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground underline"
          >
            {showGhost ? t("coach.ghost.hide") : t("coach.ghost.show")}
          </button>
        </div>
      )}
    </div>
  );
}

function QualityBadge({ label, state, value }: { label: string; state: "good" | "ok" | "bad"; value: string }) {
  const cls =
    state === "good"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : state === "bad"
      ? "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30"
      : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${cls}`}>
      <span className="font-semibold">{label}</span>
      <span className="opacity-80">{value}</span>
    </span>
  );
}

/* ────────────────────────────  Analysis (Level 1) ──────────────────────────── */

const ROI_FRAC = 0.7; // centered ROI = 70% of min(w, h)
const MAX_SAMPLE = 384; // downscale longest edge for fast pixel ops

function analyzeImage(img: HTMLImageElement): CaptureQuality {
  const { canvas, ctx, w, h } = drawScaled(img, MAX_SAMPLE);
  const roiSize = Math.floor(Math.min(w, h) * ROI_FRAC);
  const rx = Math.floor((w - roiSize) / 2);
  const ry = Math.floor((h - roiSize) / 2);
  const { data } = ctx.getImageData(rx, ry, roiSize, roiSize);

  // Luma + brightness stats
  const luma = new Float32Array(roiSize * roiSize);
  let sum = 0;
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    luma[j] = y;
    sum += y;
  }
  const mean = sum / luma.length;
  let varSum = 0;
  for (let j = 0; j < luma.length; j++) varSum += (luma[j] - mean) ** 2;
  const std = Math.sqrt(varSum / luma.length);

  // Laplacian variance (focus measure)
  const focusVariance = laplacianVariance(luma, roiSize, roiSize);

  // Framing: centroid of edge-strong pixels vs ROI center
  const framingOffset = edgeCentroidOffset(luma, roiSize, roiSize);

  // Cleanup
  canvas.width = canvas.height = 0;

  return {
    focus: focusVariance < 60 ? "blurry" : focusVariance > 250 ? "sharp" : "ok",
    focusVariance: Math.round(focusVariance),
    lighting:
      mean < 55 ? "low" : mean > 215 ? "harsh" : std < 25 ? "ok" : "good",
    brightnessMean: Math.round(mean),
    brightnessStd: Math.round(std),
    framing: framingOffset > 0.22 ? "off" : framingOffset < 0.10 ? "good" : "ok",
    coachLevel: 1,
  };
}

function drawScaled(img: HTMLImageElement, maxEdge: number) {
  const ratio = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, w, h);
  return { canvas, ctx, w, h };
}

function laplacianVariance(luma: Float32Array, w: number, h: number): number {
  // 3x3 Laplacian kernel: [0,1,0; 1,-4,1; 0,1,0]
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const v =
        luma[i - w] + luma[i + w] + luma[i - 1] + luma[i + 1] - 4 * luma[i];
      sum += v;
      sumSq += v * v;
      n++;
    }
  }
  const mean = sum / n;
  return sumSq / n - mean * mean;
}

function edgeCentroidOffset(luma: Float32Array, w: number, h: number): number {
  // Sobel magnitude → weighted centroid. Returns offset from center, normalized [0..1].
  let mx = 0;
  let my = 0;
  let mw = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const gx =
        -luma[i - w - 1] - 2 * luma[i - 1] - luma[i + w - 1] +
         luma[i - w + 1] + 2 * luma[i + 1] + luma[i + w + 1];
      const gy =
        -luma[i - w - 1] - 2 * luma[i - w] - luma[i - w + 1] +
         luma[i + w - 1] + 2 * luma[i + w] + luma[i + w + 1];
      const m = Math.abs(gx) + Math.abs(gy);
      if (m > 30) {
        mw += m;
        mx += x * m;
        my += y * m;
      }
    }
  }
  if (mw === 0) return 1;
  const cx = mx / mw;
  const cy = my / mw;
  const dx = (cx - w / 2) / w;
  const dy = (cy - h / 2) / h;
  return Math.sqrt(dx * dx + dy * dy) * 2; // *2 → ~[0..1] for typical offsets
}

/* ────────────────────────────  Overlay (per body area) ──────────────────────────── */

function overlayForArea(area: BodyArea) {
  const stroke = "currentColor";
  switch (area) {
    case "face":
    case "eyebrows_lashes":
      return (
        <ellipse cx="50" cy="62" rx="28" ry="40" fill="none" stroke={stroke} strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />
      );
    case "scalp":
      return (
        <path d="M 20 50 Q 50 10 80 50" fill="none" stroke={stroke} strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />
      );
    case "scar_site":
    case "hands":
    case "limb":
    case "abdomen":
      return (
        <rect x="22" y="32" width="56" height="62" rx="2" fill="none" stroke={stroke} strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />
      );
    case "neck_decollete":
      return (
        <path d="M 18 30 Q 50 60 82 30 L 82 95 L 18 95 Z" fill="none" stroke={stroke} strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />
      );
    default:
      return (
        <rect x="12" y="20" width="76" height="90" rx="3" fill="none" stroke={stroke} strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />
      );
  }
}